import { AxiosError } from 'axios';

/**
 * Extract a user-friendly error message from an unknown error.
 * Handles Axios errors, standard Error objects, and unknown types.
 *
 * @param err - The caught error (typed as unknown)
 * @param fallback - Default message if no better message can be extracted
 * @returns A string error message suitable for display to the user
 */
/** First readable message out of the API's `details.fields` map.
 *
 * Shape: `{ details: { fields: { field_name: ['message', ...] } } }`. Several
 * fields can fail at once; they are joined so nothing is hidden behind the
 * first one.
 */
function getFieldErrorMessage(data: Record<string, unknown> | undefined): string | undefined {
  const details = data?.['details'] as Record<string, unknown> | undefined;
  const fields = details?.['fields'] as Record<string, unknown> | undefined;
  if (!fields || typeof fields !== 'object') return undefined;
  const messages = Object.values(fields)
    .flatMap(v => (Array.isArray(v) ? v : [v]))
    .filter((v): v is string => typeof v === 'string' && v.length > 0);
  return messages.length ? messages.join(' ') : undefined;
}

const NAMED_ENTITIES: Record<string, string> = { quot: '"', amp: '&', lt: '<', gt: '>', apos: "'", nbsp: ' ' };

/**
 * Undo the API's HTML-escaping of error text.
 *
 * The backend's exception handler escapes `< > & " '` in every message
 * (CWE-79 hardening for HTML-rendering consumers), so a band label quoted
 * as "D" reached the toast as &quot;D&quot;. This app renders messages as
 * React text nodes, which never interpret HTML, so decoding is safe here
 * and the escaping stays in place for everyone else.
 */
export function decodeApiText(text: string): string {
  return text.replace(/&(#x[0-9a-f]+|#\d+|[a-z]+);/gi, (whole, body: string) => {
    const lower = body.toLowerCase();
    if (lower.startsWith('#x')) return String.fromCodePoint(parseInt(lower.slice(2), 16));
    if (lower.startsWith('#')) return String.fromCodePoint(parseInt(lower.slice(1), 10));
    return NAMED_ENTITIES[lower] ?? whole;
  });
}

export function getApiErrorMessage(err: unknown, fallback = 'An unexpected error occurred'): string {
  return decodeApiText(extractApiErrorMessage(err, fallback));
}

function extractApiErrorMessage(err: unknown, fallback: string): string {
  if (err instanceof AxiosError) {
    const data = err.response?.data as Record<string, unknown> | undefined;
    return (data?.['detail'] as string)
      || (data?.['error'] as string)
      // A DRF field error arrives inside the standard envelope as
      // details.fields — and the sibling `message` is the useless generic
      // "Validation failed". Read the field message first, or every rejected
      // form tells the user only that something was wrong, never what.
      || getFieldErrorMessage(data)
      || (data?.['message'] as string)
      || err.message
      || fallback;
  }
  if (err instanceof Error) {
    return err.message || fallback;
  }
  if (typeof err === 'string') {
    return err;
  }
  return fallback;
}
